import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
    Box, Typography, Stack, Button, Dialog, DialogContent, DialogActions,
    LinearProgress, Chip, IconButton, Tooltip, CircularProgress,
} from '@mui/material';
import RocketLaunchRoundedIcon from '@mui/icons-material/RocketLaunchRounded';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import RadioButtonUncheckedRoundedIcon from '@mui/icons-material/RadioButtonUncheckedRounded';
import ArrowForwardRoundedIcon from '@mui/icons-material/ArrowForwardRounded';
import RefreshRoundedIcon from '@mui/icons-material/RefreshRounded';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import LogoutRoundedIcon from '@mui/icons-material/LogoutRounded';
import HourglassEmptyRoundedIcon from '@mui/icons-material/HourglassEmptyRounded';
import CalendarMonthRoundedIcon from '@mui/icons-material/CalendarMonthRounded';
import CategoryRoundedIcon from '@mui/icons-material/CategoryRounded';
import RuleRoundedIcon from '@mui/icons-material/RuleRounded';
import ScheduleRoundedIcon from '@mui/icons-material/ScheduleRounded';
import EventAvailableRoundedIcon from '@mui/icons-material/EventAvailableRounded';
import GroupsRoundedIcon from '@mui/icons-material/GroupsRounded';
import AdminPanelSettingsRoundedIcon from '@mui/icons-material/AdminPanelSettingsRounded';
import VerifiedUserRoundedIcon from '@mui/icons-material/VerifiedUserRounded';
import RequestQuoteRoundedIcon from '@mui/icons-material/RequestQuoteRounded';
import { useNavigate, useLocation } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { selectUserTypeId, USER_TYPE, logout, selectEffectiveModules } from '../redux/slices/authSlice';
import { hasModule } from '../data/serverModules';
import { selectActiveEntityId } from '../redux/slices/orgSlice';
import http from '../Api/http';
import { GetEntitySetupStatus } from '../Api/Api';

const PRIMARY = '#135F93';
const PRIMARY_LIGHT = '#E4F0F9';
const GRADIENT = 'linear-gradient(135deg, #135F93 0%, #2F8EBD 100%)';

// Label and destination live together, so a step can never be named one thing
// and open another. The API's own title is only used for steps we don't know.
const STEP_META = {
    financialYear: { icon: CalendarMonthRoundedIcon, label: 'Financial Year', to: '/dashboard/settings?tab=financial-year' },
    leaveTypes: { icon: CategoryRoundedIcon, label: 'Leave Types', to: '/dashboard/leave-policy?tab=types' },
    leavePolicy: { icon: RuleRoundedIcon, label: 'Leave Policy', to: '/dashboard/leave-policy?tab=setup' },
    shifts: { icon: ScheduleRoundedIcon, label: 'Shifts', to: '/dashboard/leave-policy?tab=setup' },
    workingCalendar: { icon: EventAvailableRoundedIcon, label: 'Working Calendar', to: '/dashboard/leave-policy?tab=calendar' },
    shiftAssignment: { icon: GroupsRoundedIcon, label: 'Assign Shifts', to: '/dashboard/leave-policy?tab=shifts' },
    entityAdmin: { icon: AdminPanelSettingsRoundedIcon, label: 'Entity Admin', to: `/dashboard/roles/${USER_TYPE.ADMIN}/users` },
    complianceConfig: { icon: VerifiedUserRoundedIcon, label: 'Statutory Configuration', to: '/dashboard/payroll-setup?tab=statutory' },
    salaryStructures: { icon: RequestQuoteRoundedIcon, label: 'Salary Structure', to: '/dashboard/payroll-setup?tab=structures' },
};
const FALLBACK = { icon: RadioButtonUncheckedRoundedIcon, to: null };

const ADMIN_SETUP_STEPS = new Set(['shiftAssignment', 'salaryStructures', 'complianceConfig']);

const SETUP_MODULES = ['assign-shifts', 'salary-structures', 'statutory-deductions'];

const FORCE_REQUIRED = ADMIN_SETUP_STEPS;

const normalizeSetup = (data) => {
    if (!data) return data;
    const steps = Array.isArray(data.steps)
        ? data.steps.map((s) => (FORCE_REQUIRED.has(s.key) ? { ...s, required: true } : s))
        : [];
    const pendingRequired = steps.filter((s) => s.required && !s.done).length;
    return { ...data, steps, pendingRequired, ready: pendingRequired === 0 };
};

export default function EntitySetupGate() {
    const navigate = useNavigate();
    const location = useLocation();
    const dispatch = useDispatch();
    const userTypeId = useSelector(selectUserTypeId);
    const activeEntityId = useSelector(selectActiveEntityId);

    const isMasterAdmin = userTypeId === USER_TYPE.MASTER_ADMIN;
    const isAdmin = userTypeId === USER_TYPE.ADMIN;
    const modules = useSelector(selectEffectiveModules);
    const canConfigureSetup = isMasterAdmin || isAdmin || SETUP_MODULES.some((m) => hasModule(modules, m));

    const [status, setStatus] = useState(null);
    const [loading, setLoading] = useState(false);
    const [open, setOpen] = useState(false);
    const [autoShown, setAutoShown] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const { data: body } = await http.get(GetEntitySetupStatus);
            setStatus(body && !body.error ? normalizeSetup(body.data || null) : null);
        } catch {
            setStatus(null);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { setAutoShown(false); load(); }, [activeEntityId, load]);

    useEffect(() => {
        if (status && !status.ready) load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location.pathname]);

    useEffect(() => {
        if (status && !status.ready && !autoShown) { setOpen(true); setAutoShown(true); }
    }, [status, autoShown]);

    // Draggable launcher: position is kept as distance from the right/bottom edges so it
    // still grows leftwards on hover. A move under DRAG_THRESHOLD px counts as a click.
    const FAB_KEY = 'ara-setup-fab-pos';
    const [fabPos, setFabPos] = useState(() => {
        try {
            const v = JSON.parse(localStorage.getItem(FAB_KEY) || 'null');
            return v && Number.isFinite(v.right) && Number.isFinite(v.bottom) ? v : { right: 28, bottom: 28 };
        } catch { return { right: 28, bottom: 28 }; }
    });
    const fabRef = useRef(null);
    const dragRef = useRef(null);
    const justDragged = useRef(false);

    const clampPos = useCallback((pos) => {
        const w = fabRef.current?.offsetWidth || 58;
        const h = fabRef.current?.offsetHeight || 58;
        return {
            right: Math.min(Math.max(8, pos.right), Math.max(8, window.innerWidth - w - 8)),
            bottom: Math.min(Math.max(8, pos.bottom), Math.max(8, window.innerHeight - h - 8)),
        };
    }, []);

    useEffect(() => {
        const onResize = () => setFabPos((p) => clampPos(p));
        window.addEventListener('resize', onResize);
        return () => window.removeEventListener('resize', onResize);
    }, [clampPos]);

    const onFabPointerDown = (e) => {
        if (e.button != null && e.button !== 0) return;
        dragRef.current = { x: e.clientX, y: e.clientY, start: fabPos, moved: false };
        e.currentTarget.setPointerCapture?.(e.pointerId);
    };
    const onFabPointerMove = (e) => {
        const d = dragRef.current;
        if (!d) return;
        const dx = e.clientX - d.x;
        const dy = e.clientY - d.y;
        if (!d.moved && Math.hypot(dx, dy) < 5) return;
        d.moved = true;
        setFabPos(clampPos({ right: d.start.right - dx, bottom: d.start.bottom - dy }));
    };
    const onFabPointerUp = (e) => {
        const d = dragRef.current;
        dragRef.current = null;
        e.currentTarget.releasePointerCapture?.(e.pointerId);
        if (d?.moved) {
            justDragged.current = true;
            setTimeout(() => { justDragged.current = false; }, 0);
            try { localStorage.setItem(FAB_KEY, JSON.stringify(fabPos)); } catch { /* storage unavailable */ }
        }
    };

    if (!status || status.ready) return null;

    const steps = Array.isArray(status.steps) ? status.steps : [];
    const pendingRequired = Number(status.pendingRequired) || 0;
    const completed = Number(status.completedSteps) || 0;
    const total = Number(status.totalSteps) || steps.length || 1;
    const pct = Math.round((completed / total) * 100);
    const entityName = status.entityName || 'this entity';

    const corePending = steps.some((s) => s.required && !s.done && !ADMIN_SETUP_STEPS.has(s.key));

    if (!canConfigureSetup) {
        if (!corePending) return null;
        return (
            <Box sx={{ position: 'fixed', inset: 0, zIndex: 2000, bgcolor: 'rgba(15,23,42,0.55)', backdropFilter: 'blur(4px)', display: 'flex', alignItems: 'center', justifyContent: 'center', p: 2 }}>
                <Box sx={{ width: '100%', maxWidth: 460, bgcolor: '#fff', borderRadius: '16px', overflow: 'hidden', boxShadow: '0 30px 60px -20px rgba(15,23,42,0.5)' }}>
                    <Box sx={{ background: GRADIENT, p: 3, color: '#fff', textAlign: 'center' }}>
                        <HourglassEmptyRoundedIcon sx={{ fontSize: 40 }} />
                        <Typography sx={{ fontSize: 19, fontWeight: 800, mt: 1 }}>Setup in progress</Typography>
                    </Box>
                    <Box sx={{ p: 3, textAlign: 'center' }}>
                        <Typography sx={{ fontSize: 14, color: '#334155', lineHeight: 1.6 }}>
                            <strong>{entityName}</strong> isn&apos;t quite ready yet — your administrator is still finishing its setup.
                            You&apos;ll be able to sign in and use the app once it&apos;s done.
                        </Typography>
                        <Button
                            onClick={() => dispatch(logout())}
                            startIcon={<LogoutRoundedIcon sx={{ fontSize: 18 }} />}
                            sx={{ mt: 3, height: 44, px: 3, bgcolor: PRIMARY, color: '#fff', fontWeight: 700, borderRadius: '9px', textTransform: 'none', boxShadow: 'none', '&:hover': { bgcolor: '#0E4A75' } }}
                        >
                            Sign out
                        </Button>
                    </Box>
                </Box>
            </Box>
        );
    }

    const go = (to) => { if (to) { navigate(to); setOpen(false); } };

    return (
        <>
            {!open && (
                <Box
                    ref={fabRef}
                    onPointerDown={onFabPointerDown}
                    onPointerMove={onFabPointerMove}
                    onPointerUp={onFabPointerUp}
                    onPointerCancel={onFabPointerUp}
                    onClick={() => { if (justDragged.current) return; load(); setOpen(true); }}
                    sx={{ position: 'fixed', right: fabPos.right, bottom: fabPos.bottom, zIndex: 1400, touchAction: 'none', cursor: 'grab', '&:active': { cursor: 'grabbing' }, userSelect: 'none' }}
                >
                    <>
                        <Button
                            aria-label={`Finish setup, ${pendingRequired} left`}
                            sx={{
                                '@keyframes setupGlow': {
                                    '0%, 100%': { boxShadow: '0 0 0 4px rgba(47,142,189,0.16), 0 8px 20px -6px rgba(19,95,147,0.5)' },
                                    '50%': { boxShadow: '0 0 0 8px rgba(47,142,189,0.10), 0 8px 22px -6px rgba(19,95,147,0.55)' },
                                },
                                '@keyframes setupRocket': {
                                    '0%, 100%': { transform: 'translateY(0) rotate(0deg)', filter: 'drop-shadow(0 0 2px rgba(255,255,255,0.35))' },
                                    '50%': { transform: 'translateY(-2px) rotate(-8deg)', filter: 'drop-shadow(0 0 4px rgba(255,255,255,0.6))' },
                                },
                                position: 'relative',
                                cursor: 'inherit',
                                minWidth: 0,
                                width: 58,
                                height: 58,
                                p: 0,
                                pr: '15px', // (58 - 28) / 2 — keeps the icon in the same spot collapsed or expanded
                                gap: 0,
                                justifyContent: 'flex-end',
                                alignItems: 'center',
                                overflow: 'visible',
                                background: GRADIENT,
                                color: '#fff',
                                borderRadius: '999px',
                                whiteSpace: 'nowrap',
                                animation: 'setupGlow 3s ease-in-out infinite',
                                transition: 'width .35s cubic-bezier(.4,0,.2,1), padding .35s',
                                '& .setup-label': {
                                    maxWidth: 0, opacity: 0, overflow: 'hidden', fontWeight: 800, fontSize: 13,
                                    transition: 'max-width .35s cubic-bezier(.4,0,.2,1), opacity .25s, margin .35s',
                                },
                                '& .setup-icon': { animation: 'setupRocket 2.2s ease-in-out infinite', flexShrink: 0 },
                                '&:hover, &:focus-visible': {
                                    width: 'auto',
                                    pl: 2.6,
                                    pr: '15px',
                                    '& .setup-icon': { animation: 'none', transform: 'none', filter: 'drop-shadow(0 0 3px rgba(255,255,255,0.45))' },
                                    filter: 'brightness(0.98)',
                                    '& .setup-label': { maxWidth: 220, opacity: 1, mr: 1 },
                                },
                            }}
                        >
                            <Box className="setup-label" component="span">
                                Finish setup · {pendingRequired} left
                            </Box>
                            <RocketLaunchRoundedIcon className="setup-icon" sx={{ fontSize: 28 }} />
                        </Button>
                    </>
                </Box>
            )}

            <Dialog
                open={open}
                onClose={() => setOpen(false)}
                maxWidth="sm"
                fullWidth
                slotProps={{ paper: { sx: { borderRadius: '16px', overflow: 'hidden' } } }}
            >
                <Box sx={{ background: GRADIENT, p: 2.6, color: '#fff', position: 'relative' }}>
                    <IconButton onClick={() => setOpen(false)} size="small" sx={{ position: 'absolute', top: 10, right: 10, color: 'rgba(255,255,255,0.85)', '&:hover': { color: '#fff', bgcolor: 'rgba(255,255,255,0.14)' } }}>
                        <CloseRoundedIcon sx={{ fontSize: 19 }} />
                    </IconButton>
                    <Stack direction="row" spacing={1.4} sx={{ alignItems: 'center' }}>
                        <Box sx={{ width: 42, height: 42, borderRadius: '11px', bgcolor: 'rgba(255,255,255,0.18)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <RocketLaunchRoundedIcon sx={{ fontSize: 23 }} />
                        </Box>
                        <Box>
                            <Typography sx={{ fontSize: 17, fontWeight: 800 }}>Set up {entityName}</Typography>
                            <Typography sx={{ fontSize: 12.5, opacity: 0.9 }}>
                                {pendingRequired > 0 ? `${pendingRequired} required step${pendingRequired === 1 ? '' : 's'} left before it can run` : 'Almost there — finish the optional steps'}
                            </Typography>
                        </Box>
                    </Stack>
                    <Box sx={{ mt: 1.8 }}>
                        <Stack direction="row" sx={{ justifyContent: 'space-between', mb: 0.6 }}>
                            <Typography sx={{ fontSize: 11, fontWeight: 700, opacity: 0.9 }}>{completed} of {total} done</Typography>
                            <Typography sx={{ fontSize: 11, fontWeight: 800 }}>{pct}%</Typography>
                        </Stack>
                        <LinearProgress variant="determinate" value={pct} sx={{ height: 7, borderRadius: 5, bgcolor: 'rgba(255,255,255,0.25)', '& .MuiLinearProgress-bar': { bgcolor: '#fff', borderRadius: 5 } }} />
                    </Box>
                </Box>

                <DialogContent sx={{ p: 1.5 }}>
                    <Stack spacing={0.8}>
                        {steps.map((s) => {
                            const meta = STEP_META[s.key] || FALLBACK;
                            const Icon = s.done ? CheckCircleRoundedIcon : meta.icon;
                            return (
                                <Stack
                                    key={s.key}
                                    direction="row"
                                    spacing={1.4}
                                    sx={{
                                        alignItems: 'center', p: 1.3, borderRadius: '11px',
                                        border: '1px solid', borderColor: s.done ? '#DCFCE7' : '#EEF1F6',
                                        bgcolor: s.done ? '#F5FDF8' : '#fff',
                                    }}
                                >
                                    <Box sx={{ width: 38, height: 38, borderRadius: '10px', flexShrink: 0, bgcolor: s.done ? '#DCFCE7' : PRIMARY_LIGHT, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                        <Icon sx={{ fontSize: 20, color: s.done ? '#16A34A' : PRIMARY }} />
                                    </Box>
                                    <Box sx={{ minWidth: 0, flex: 1 }}>
                                        <Stack direction="row" spacing={0.7} sx={{ alignItems: 'center', flexWrap: 'wrap' }}>
                                            <Typography sx={{ fontSize: 13.5, fontWeight: 700, color: '#0F172A' }}>{meta.label || s.title}</Typography>
                                            {!s.required && <Chip label="Optional" size="small" sx={{ height: 17, fontSize: 9, fontWeight: 700, bgcolor: '#F1F5F9', color: '#64748B' }} />}
                                        </Stack>
                                        <Typography sx={{ fontSize: 11.5, color: '#94A3B8', lineHeight: 1.35 }}>{s.detail || s.description}</Typography>
                                    </Box>
                                    {s.done ? (
                                        <Chip label="Done" size="small" sx={{ flexShrink: 0, height: 24, fontSize: 10.5, fontWeight: 800, bgcolor: '#DCFCE7', color: '#16A34A' }} />
                                    ) : meta.to ? (
                                        <Tooltip arrow title={`Go to ${meta.label || s.title}`}>
                                            <IconButton onClick={() => go(meta.to)} size="small" sx={{ flexShrink: 0, bgcolor: PRIMARY_LIGHT, color: PRIMARY, borderRadius: '9px', width: 34, height: 34, '&:hover': { bgcolor: '#D0E5F3' } }}>
                                                <ArrowForwardRoundedIcon sx={{ fontSize: 18 }} />
                                            </IconButton>
                                        </Tooltip>
                                    ) : null}
                                </Stack>
                            );
                        })}
                    </Stack>
                </DialogContent>

                <DialogActions sx={{ px: 2, py: 1.8, borderTop: '1px solid #F1F3F7', gap: 1, flexWrap: 'wrap' }}>
                    <Button
                        onClick={load}
                        disabled={loading}
                        startIcon={loading ? <CircularProgress size={14} /> : <RefreshRoundedIcon sx={{ fontSize: 17 }} />}
                        sx={{ textTransform: 'none', fontWeight: 700, fontSize: 12.5, color: '#475569', bgcolor: '#F1F5F9', borderRadius: '8px', height: 40, px: 2, '&:hover': { bgcolor: '#E2E8F0' } }}
                    >
                        Re-check
                    </Button>
                    <Box sx={{ flex: 1 }} />
                    {isMasterAdmin ? (
                        <Button onClick={() => setOpen(false)} sx={{ textTransform: 'none', fontWeight: 700, fontSize: 12.5, color: '#64748B', borderRadius: '8px', height: 40, px: 2 }}>
                            Skip for now
                        </Button>
                    ) : (
                        <Typography sx={{ fontSize: 11.5, color: '#94A3B8', alignSelf: 'center', mr: 0.5 }}>
                            Complete the required steps to start using {entityName}.
                        </Typography>
                    )}
                    <Button
                        onClick={() => setOpen(false)}
                        sx={{ background: GRADIENT, color: '#fff', textTransform: 'none', fontWeight: 800, fontSize: 12.5, borderRadius: '8px', height: 40, px: 2.4, boxShadow: 'none', '&:hover': { filter: 'brightness(0.96)' } }}
                    >
                        Got it
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
}
